import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useToken } from '../common/helper';
import { GetSettingsProfile } from '../apis/settings';

type ProfileQueryResponse = Awaited<ReturnType<typeof GetSettingsProfile>>;

// Fixed key (not keyed on the token value) — every consumer shares one
// cached fetch/in-flight request regardless of which `useToken()` instance's
// `token` state happens to be current. `useToken()`'s `token` React state is
// a per-call-site snapshot that can go stale relative to localStorage after a
// silent background refresh (RefreshTokenContext writes the refreshed token
// straight to localStorage without updating every mounted `useToken()`
// instance's state) — so the queryFn always re-reads the live token via
// `checkTokenExpiry()` at call time instead of trusting a closed-over value.
export const PROFILE_QUERY_KEY = ['profile'] as const;

export function useProfileQuery(
  enabled = true
): UseQueryResult<ProfileQueryResponse> {
  const { token, checkTokenExpiry } = useToken();

  return useQuery<ProfileQueryResponse>({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: () => {
      const liveToken = checkTokenExpiry();
      if (!liveToken) {
        return Promise.reject(new Error('No token available'));
      }
      return GetSettingsProfile(liveToken);
    },
    enabled: enabled && !!token,
    staleTime: 60 * 1000,
    retry: false,
  });
}
